'use client';

import { useState, MouseEvent, useEffect, useRef, useCallback } from 'react';
import Image from 'next/image';
import { X, Loader2, Maximize2 } from 'lucide-react';
import styles from './GalleryGrid.module.css';
import { getGalleryImages, GalleryImage } from '@/app/(frontend)/galeri/actions';

interface GalleryGridProps {
    initialImages: GalleryImage[];
    hasNextPage: boolean;
    totalDocs: number;
}

export default function GalleryGrid({ initialImages, hasNextPage: initialHasNextPage, totalDocs }: GalleryGridProps) {
    const [images, setImages] = useState<GalleryImage[]>(initialImages);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(initialHasNextPage);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
    
    const observerTarget = useRef<HTMLDivElement>(null);

    const loadMoreImages = useCallback(async () => {
        if (isLoading || !hasMore) return;

        setIsLoading(true);
        const nextPage = page + 1;
        
        try {
            const result = await getGalleryImages(nextPage);
            setImages((prev) => [...prev, ...result.images]);
            setPage(nextPage);
            setHasMore(result.hasNextPage);
        } catch (error) {
            console.error('Error loading more gallery images:', error);
        } finally {
            setIsLoading(false);
        }
    }, [page, hasMore, isLoading]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !isLoading) {
                    loadMoreImages();
                }
            },
            { threshold: 1.0 }
        );

        if (observerTarget.current) {
            observer.observe(observerTarget.current);
        }

        return () => observer.disconnect();
    }, [loadMoreImages, hasMore, isLoading]);

    const closeModal = (e?: MouseEvent) => {
        if (e) e.stopPropagation();
        setSelectedImage(null);
    };

    if (!images || images.length === 0) {
        return (
            <section className={`container ${styles.gallerySection}`}>
                <div className={styles.emptyState}>
                    <h3 className={styles.emptyTitle}>Galeri Kosong</h3>
                    <p>Dokumentasi foto akan segera ditambahkan.</p>
                </div>
            </section>
        );
    }

    return (
        <section className={`container ${styles.gallerySection}`}>
            <div className={styles.galleryGrid}>
                {images.map((img) => (
                    <div
                        key={img.id}
                        className={styles.galleryItem}
                        onClick={() => setSelectedImage(img)}
                    >
                        {img.mime_type?.startsWith('video/') ? (
                            <video
                                src={img.image_url}
                                className={styles.galleryImage}
                                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                                autoPlay
                                muted
                                loop
                                playsInline
                            />
                        ) : (
                            <Image
                                src={img.image_url}
                                alt={`${img.title} - Dokumentasi Layanan Ekspedisi Cargo PT Trans Nusa Pasifik`}
                                title={`${img.title} - Dokumentasi Layanan Ekspedisi Cargo PT Trans Nusa Pasifik`}
                                fill
                                className={styles.galleryImage}
                                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                            />
                        )}
                        <div className={styles.overlay}>
                            <div className={styles.overlayIconWrapper}>
                                <Maximize2 size={32} className={styles.overlayIcon} />
                            </div>
                            <h3 className={styles.imageTitle}>{img.title}</h3>
                        </div>
                    </div>
                ))}
            </div>

            {/* Infinite Scroll Sentinel & Loader */}
            <div ref={observerTarget} className={styles.sentinel}>
                {isLoading && (
                    <div className={styles.loaderContainer}>
                        <Loader2 className={styles.spinner} size={32} />
                        <p>Memuat lebih banyak foto...</p>
                    </div>
                )}
            </div>

            {/* Fullscreen Lightbox Modal */}
            {selectedImage && (
                <div className={styles.lightbox} onClick={() => closeModal()}>
                    <button
                        className={styles.lightboxClose}
                        onClick={(e) => closeModal(e)}
                        aria-label="Close Gallery Fullscreen"
                    >
                        <X size={32} />
                    </button>

                    <div
                        className={styles.lightboxContent}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {selectedImage.mime_type?.startsWith('video/') ? (
                            <video
                                src={selectedImage.image_url}
                                className={styles.lightboxImage}
                                style={{ maxHeight: '90vh', maxWidth: '90vw', objectFit: 'contain' }}
                                controls
                                autoPlay
                                playsInline
                            />
                        ) : (
                            <Image
                                src={selectedImage.image_url}
                                alt={`${selectedImage.title} - Dokumentasi Layanan Ekspedisi Cargo PT Trans Nusa Pasifik`}
                                title={`${selectedImage.title} - Dokumentasi Layanan Ekspedisi Cargo PT Trans Nusa Pasifik`}
                                fill
                                className={styles.lightboxImage}
                                sizes="100vw"
                                priority
                            />
                        )}
                        <div className={styles.lightboxCaption}>
                            {selectedImage.title}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
